// Labels for text that comes straight from catalog and trip data (#606).
// Catalog fields carry slugs ("portland-or", "hiroo", "sf-bay"), source ids
// ("google_places") and research notes ("[VERIFY: …]", "curl 2026-07-24").
// Every surface that prints one of those as a label runs it through here,
// at render time; the catalog JSON stays as it is.

const LABEL_OVERRIDES: Record<string, string> = {
  la: 'Los Angeles',
  'sf-bay': 'SF Bay Area',
  'other-ca': 'California',
  'napa-sonoma': 'Napa & Sonoma',
  nyc: 'New York City',
  'new-york': 'New York',
  google_places: 'Google',
  google: 'Google',
  googlemaps: 'Google Maps',
  wikimedia: 'Wikimedia Commons',
  tabelog: 'Tabelog',
  michelin: 'Michelin',
  yelp: 'Yelp',
  unsplash: 'Unsplash',
  openverse: 'Openverse',
  pexels: 'Pexels',
  europeana: 'Europeana',
  // Catalog types, when one is printed as a label.
  attractions: 'Sights',
  restaurants: 'Restaurants',
  bars: 'Bars',
  hotels: 'Hotels',
  experiences: 'Experiences',
  shopping: 'Shopping',
  destinations: 'Places',
};

const US_STATES = new Set([
  'al', 'ak', 'az', 'ar', 'ca', 'co', 'ct', 'de', 'fl', 'ga', 'hi', 'id', 'il', 'in', 'ia', 'ks', 'ky', 'la', 'me', 'md',
  'ma', 'mi', 'mn', 'ms', 'mo', 'mt', 'ne', 'nv', 'nh', 'nj', 'nm', 'ny', 'nc', 'nd', 'oh', 'ok', 'or', 'pa', 'ri', 'sc',
  'sd', 'tn', 'tx', 'ut', 'vt', 'va', 'wa', 'wv', 'wi', 'wy', 'dc',
]);

const SMALL_WORDS = new Set(['and', 'of', 'the', 'de', 'la', 'del', 'di', 'en', 'no', 'y']);

function titleWord(w: string, i: number): string {
  if (i > 0 && SMALL_WORDS.has(w)) return w;
  return w.charAt(0).toUpperCase() + w.slice(1);
}

/**
 * A slug or id as a label: "portland-or" → "Portland, OR", "hiroo" →
 * "Hiroo", "GOOGLE_PLACES" → "Google", "new-york" → "New York". Text that
 * already has capitals or spaces ("Île-de-France", "Kailua-Kona, Big
 * Island") comes back unchanged.
 */
export function humanize(v: string | null | undefined): string {
  if (typeof v !== 'string') return '';
  const s = v.trim().replace(/^custom:/, '');
  if (!s) return '';
  const lower = s.toLowerCase();
  if (LABEL_OVERRIDES[lower]) return LABEL_OVERRIDES[lower];
  const snake = /^[A-Z0-9]+(?:_[A-Z0-9]+)+$/.test(s);
  const slug = /^[a-z0-9]+(?:[-_][a-z0-9]+)*$/.test(s);
  if (!snake && !slug) return s;
  const words = lower.split(/[-_]+/).filter(Boolean);
  const last = words[words.length - 1];
  if (words.length > 1 && last && US_STATES.has(last)) {
    return `${words.slice(0, -1).map(titleWord).join(' ')}, ${last.toUpperCase()}`;
  }
  return words.map(titleWord).join(' ');
}

/** "1 night", "3 nights"; the plural form defaults to word + "s". */
export function plural(n: number, word: string, pluralWord?: string): string {
  return `${n} ${n === 1 ? word : (pluralWord ?? `${word}s`)}`;
}

// Research placeholders and pipeline provenance that must not reach a reader.
const PLACEHOLDER_RE = /\s*\[(?:VERIFY|TODO|TBD|CHECK|FIXME)\b[^\]]*\]/gi;
// A parenthetical that only records how a fact was fetched: "(curl
// 2026-07-24)", "(Michelin 'Need to Know', curl 2026-07-24)", "(Chrome-verified
// 2026-05-06)", "(boundary static across Jun 14 -> Jul 9 2026 reads)". A tool
// name marks one outright; "verified" / "fetched" / "read" need a year too, so
// "(pulled pork)" stays.
const PAREN_RE = /\s*\(([^()]*)\)/g;
const TOOL_RE = /\b(?:curl|chrome|webfetch|wayback)\b|scripts\/|\.(?:py|mjs)\b/i;
const FETCH_WORD_RE = /\b(?:verified|re-?verified|fetched|pulled|re-?read|reads?)\b/i;
const YEAR_RE = /\b20\d\d\b/;
function dropProvenanceParens(s: string): string {
  return s.replace(PAREN_RE, (m, inner: string) =>
    TOOL_RE.test(inner) || (FETCH_WORD_RE.test(inner) && YEAR_RE.test(inner)) ? '' : m,
  );
}
// Inline provenance stamps: "curl-verified 2026-07-24", "Chrome-verified via …
// 2026-05-06".
const PROVENANCE_INLINE_RE =
  /,?\s*\b(?:curl|chrome|webfetch)(?:[- ](?:verified|fetched|read))?(?:\s+via\s+[^,;.]+?)?\s+\d{4}-\d{2}-\d{2}\b/gi;
const SCRIPT_RE = /`?\bscripts\/[\w./-]+`?/g;
const JARGON: Array<[RegExp, string]> = [
  [/\bBATCH-to-horizon\b/g, 'batch release'],
  [/\bMONTHLY BULK\b/g, 'monthly batch'],
];

/**
 * Catalog prose with research placeholders, fetch provenance and script
 * names taken out. Leaves the facts; drops the notes about how they were
 * gathered.
 */
export function cleanResearchText(v: string | null | undefined): string {
  if (typeof v !== 'string') return '';
  let s = v.replace(PLACEHOLDER_RE, '');
  s = dropProvenanceParens(s);
  s = s.replace(PROVENANCE_INLINE_RE, '');
  s = s.replace(SCRIPT_RE, '');
  for (const [re, to] of JARGON) s = s.replace(re, to);
  return s
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/[ \t]+([,.;:])/g, '$1')
    .replace(/([,;])\1+/g, '$1')
    .replace(/\(\s*\)/g, '')
    .trim();
}

/** A place label (region, neighborhood) cleaned and humanized. */
export function placeLabel(v: string | null | undefined): string {
  return humanize(cleanResearchText(v));
}
